import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import purchaseReportService from '../../../../services/purchaseReportService';
import PriceTrackingShell from './PriceTrackingShell';
import usePriceTrackingFilters from './usePriceTrackingFilters';
import { formatDateLocaleLima, formatPrice, formatPct, canOpenPurchaseProductDetail } from './shared';

export default function PriceTrackingAlertsPage() {
  const navigate = useNavigate();
  const { filters, updateFilter, buildParams, suppliers, products } = usePriceTrackingFilters();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await purchaseReportService.getPriceTrackingDashboard(buildParams());
      if (res.success) {
        setRows(res.data?.price_spikes || []);
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al cargar', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al cargar alertas', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PriceTrackingShell
      title="Alertas de precio"
      subtitle="Insumos cuyo último precio subió por encima del umbral configurado."
      filters={filters}
      updateFilter={updateFilter}
      suppliers={suppliers}
      products={products}
      onQuery={loadData}
      loading={loading}
      toast={toast}
      onToastClose={() => setToast({ ...toast, isOpen: false })}
    >
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-amber-200">
        <div className="px-5 py-4 bg-amber-50 border-b border-amber-100">
          <h2 className="font-semibold text-amber-900">Precios en alza</h2>
          <p className="text-sm text-amber-800">
            Umbral: {filters.spikeThreshold}% · {formatDateLocaleLima(filters.dateFrom)} — {formatDateLocaleLima(filters.dateTo)}
          </p>
        </div>
        {loading ? (
          <p className="p-8 text-center text-gray-500">Cargando...</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-gray-500">No hay alertas en el período con el umbral actual.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Insumo</th>
                <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Anterior</th>
                <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Último</th>
                <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Promedio</th>
                <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Δ%</th>
                <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Última compra</th>
                <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Proveedor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr
                  key={`${row.item_type || 'product'}:${row.item_id ?? row.product_id}`}
                  className={canOpenPurchaseProductDetail(row) ? 'hover:bg-amber-50/50 cursor-pointer' : 'hover:bg-amber-50/30'}
                  onClick={() => {
                    if (!canOpenPurchaseProductDetail(row)) return;
                    navigate(`/compras/reports/price-tracking/products/${row.product_id ?? row.item_id}`);
                  }}
                >
                  <td className="px-4 py-3 font-medium">{row.product_name}</td>
                  <td className="px-4 py-3 text-right">{formatPrice(row.previous_price)}</td>
                  <td className="px-4 py-3 text-right font-semibold">{formatPrice(row.last_price)}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{formatPrice(row.avg_price)}</td>
                  <td className="px-4 py-3 text-right text-red-600 font-bold">{formatPct(row.variation_pct)}</td>
                  <td className="px-4 py-3 text-gray-600">{formatDateLocaleLima(row.last_date)} · {row.last_document_code}</td>
                  <td className="px-4 py-3">{row.last_supplier_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </PriceTrackingShell>
  );
}
